/**
 * RecoveryPathSimulator.jsx — iter780 (Feb 2026)
 *
 * "BHI Recovery Path" симулатор. Обратното на What-If: пациентът
 * използва плъзгачи за интервенции (target тегло, target vit D,
 * target HOMA-IR, target hs-CRP, target движение, target сън,
 * target талия, target алкохол) и вижда в реално време как биха
 * се повишили BHI композитният скор + всеки от 5-те метаболитни
 * компонента. НИЩО не се записва в базата.
 *
 * След симулация, пациентът може да отвори директно BTRE/BPP
 * плановете за actionable recovery steps ("diagnosis → recovery").
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Loader2, TrendingUp, RefreshCcw, ArrowRight, Info,
} from "lucide-react";
import { useAuth } from "@/App";

const INTERVENTIONS = [
  { key: "waist_cm",           label: "Талия",                unit: "cm",        min: 60,  max: 130, step: 1,   direction: "lower",  hint: "Целете < 88 (жени) / < 94 (мъже)" },
  { key: "weight_kg",          label: "Тегло",                unit: "kg",        min: 40,  max: 150, step: 1,   direction: "either", hint: "BMI 20-25 е оптимално" },
  { key: "vitamin_d",          label: "Витамин D",            unit: "",          min: 20,  max: 100, step: 1,   direction: "higher", hint: "Оптимум: 60-80 ng/mL" },
  { key: "homa_ir",            label: "HOMA-IR",              unit: "",          min: 0.5, max: 5.0, step: 0.1, direction: "lower",  hint: "Здравословен < 1.5" },
  { key: "hs_crp",             label: "hs-CRP",               unit: "mg/L",      min: 0.1, max: 10,  step: 0.1, direction: "lower",  hint: "Ниско възпаление < 1.0" },
  { key: "exercise_min_week",  label: "Движение",             unit: "мин/седм",  min: 0,   max: 400, step: 15,  direction: "higher", hint: "≥ 150 мин седмично" },
  { key: "sleep_hours",        label: "Сън",                  unit: "часа",      min: 5,   max: 10,  step: 0.5, direction: "higher", hint: "7-9 часа" },
  { key: "alcohol_units_week", label: "Алкохол",              unit: "ед./седм",  min: 0,   max: 21,  step: 1,   direction: "lower",  hint: "0-3 единици максимум" },
  // iter790 — DUTCH estrogen metabolite sliders
  { key: "dutch_ratio_2_16",     label: "DUTCH: 2:16-OH ratio", unit: "",           min: 0.5, max: 4.0, step: 0.1, direction: "higher", hint: "> 2.0 е защитен; < 1.5 е нежелан" },
  { key: "dutch_methylation_pct",label: "DUTCH: метилация",     unit: "%",          min: 20,  max: 100, step: 5,   direction: "higher", hint: "≥ 70% за добра детоксикация" },
  { key: "e_4_ohe1",             label: "DUTCH: 4-OH-E1",       unit: "ng/mg",      min: 0.1, max: 2.0, step: 0.05,direction: "lower",  hint: "< 0.5 е желано (DNA-adducts риск)" },
];

const COMPONENT_LABEL_BG = {
  insulin:      "Инсулинова среда",
  igf1:         "IGF-1 сигналинг",
  inflammation: "Възпалителен профил",
  estrogen:     "Естрогенна среда",
  adiposity:    "Адипозност",
};

const TWIN_LABEL_BG = {
  inflammation:      "възпалителен близнак",
  hormonal:          "хормонален близнак",
  metabolic_insulin: "метаболитно-инсулинов близнак",
};

function DeltaBadge({ before, after, larger = false }) {
  if (before == null || after == null) return null;
  const d = after - before;
  const cls = d === 0
    ? "bg-slate-100 text-slate-600 ring-slate-300"
    : d > 0
      ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
      : "bg-rose-100 text-rose-800 ring-rose-300";
  const arrow = d === 0 ? "±0" : d > 0 ? `+${d}` : `−${Math.abs(d)}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 font-bold ${cls} ${larger ? "text-sm" : "text-[11px]"}`}
      data-testid="recovery-delta-badge"
    >
      <span className="tabular-nums">{before} → {after}</span>
      <span className="tabular-nums">({arrow})</span>
    </span>
  );
}

export default function RecoveryPathSimulator({ isDark = false, bg = true }) {
  const { API, user } = useAuth();
  const [current, setCurrent] = useState({});     // baseline values from server
  const [targets, setTargets] = useState({});     // user-selected sliders
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState(null);

  // Load current baseline once (uses /breast-intelligence/snapshot which
  // returns current ctx anthropometry + labs; we only need a hint for slider
  // defaults, so a lightweight approach is fine).
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // Trigger a no-op /recovery-path call to fetch current_values.
        const { data } = await axios.post(
          `${API}/breast-intelligence/recovery-path`,
          { targets: { vitamin_d: 999 } },  // sentinel — will fail range clamp but return baseline
          { timeout: 10000 },
        ).catch(err => ({ data: err?.response?.data }));
        if (data?.current_values) setCurrent(data.current_values);
      } catch { /* noop */ }
    })();
  }, [API, user]);

  const setTarget = (key, val) => {
    setTargets((t) => ({ ...t, [key]: val }));
  };
  const clearTarget = (key) => {
    setTargets((t) => { const n = { ...t }; delete n[key]; return n; });
  };
  const clearAll = () => { setTargets({}); setResult(null); };

  const run = useCallback(async () => {
    if (!user || Object.keys(targets).length === 0) { setResult(null); return; }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/breast-intelligence/recovery-path`,
        { targets },
        { timeout: 20000 },
      );
      setResult(data);
      if (data?.current_values) setCurrent(data.current_values);
    } catch (e) {
      setError(e?.response?.data?.detail || "Грешка при симулация");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [API, user, targets]);

  // Debounce 500 ms
  useEffect(() => {
    if (Object.keys(targets).length === 0) { setResult(null); return; }
    const t = setTimeout(run, 500);
    return () => clearTimeout(t);
  }, [targets, run]);

  const outerCls = isDark
    ? "bg-slate-900/60 border border-emerald-500/40 ring-1 ring-emerald-500/20"
    : "bg-gradient-to-br from-emerald-50/60 to-white border border-emerald-200";

  if (!user) return null;

  const activeCount = Object.keys(targets).length;

  return (
    <section
      data-testid="recovery-path-simulator"
      className={`p-5 rounded-2xl ${outerCls} mb-5`}
    >
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className={`text-[10px] uppercase font-bold tracking-widest mb-0.5 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
            Възстановителен път
          </div>
          <h3 className={`text-base font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            <TrendingUp className="inline w-4 h-4 mr-1 -mt-0.5" />
            {bg ? "Симулатор на възстановяване — „какво ако постигна…\"" : "Recovery Path Simulator"}
          </h3>
          <p className={`text-[12px] mt-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg
              ? "Задайте цели за интервенция и вижте как биха се повишили BHI и метаболитните компоненти."
              : "Set intervention targets and see how BHI + metabolic components would improve."}
          </p>
        </div>
        {activeCount > 0 && (
          <button
            onClick={clearAll}
            data-testid="recovery-clear-btn"
            className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg ${isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
          >
            <RefreshCcw className="w-3 h-3" /> {bg ? "Изчисти" : "Clear"}
          </button>
        )}
      </header>

      {/* Sliders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {INTERVENTIONS.map((iv) => {
          const cur = current[iv.key];
          const val = targets[iv.key] != null ? targets[iv.key] : (cur != null ? cur : iv.min);
          const isActive = targets[iv.key] != null;
          return (
            <div key={iv.key} className={`p-3 rounded-xl ${isDark ? (isActive ? "bg-emerald-500/10 ring-1 ring-emerald-500/30" : "bg-slate-800/60") : (isActive ? "bg-emerald-50 ring-1 ring-emerald-200" : "bg-white ring-1 ring-slate-200")}`}>
              <div className="flex items-baseline justify-between mb-1.5">
                <div>
                  <span className={`text-[12px] font-bold ${isDark ? "text-slate-100" : "text-slate-800"}`}>{iv.label}</span>
                  {iv.unit && <span className={`text-[10px] ml-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>({iv.unit})</span>}
                </div>
                <div className={`text-[11px] tabular-nums ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  {cur != null && !isActive && (
                    <span className={isDark ? "text-slate-500" : "text-slate-400"}>сега: {Number(cur).toFixed(iv.step < 1 ? 1 : 0)}</span>
                  )}
                  {isActive && (
                    <span className="font-bold text-emerald-600">→ {Number(val).toFixed(iv.step < 1 ? 1 : 0)} {iv.unit}</span>
                  )}
                </div>
              </div>
              <input
                type="range"
                min={iv.min}
                max={iv.max}
                step={iv.step}
                value={val}
                onChange={(e) => setTarget(iv.key, Number(e.target.value))}
                data-testid={`recovery-slider-${iv.key}`}
                className="w-full accent-emerald-500"
              />
              <div className="flex items-center justify-between mt-1">
                <span className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>{iv.hint}</span>
                {isActive && (
                  <button
                    onClick={() => clearTarget(iv.key)}
                    data-testid={`recovery-clear-${iv.key}`}
                    className="text-[10px] text-slate-500 hover:text-rose-600"
                  >
                    премахни
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Result */}
      {loading && (
        <div className={`flex items-center gap-2 text-[12px] ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          <Loader2 className="w-4 h-4 animate-spin" />
          {bg ? "Изчисляваме възстановителния път..." : "Simulating recovery..."}
        </div>
      )}

      {error && <div className="text-[12px] text-rose-600" data-testid="recovery-error">{error}</div>}

      {result && result.unchanged && (
        <div className={`text-[12px] p-3 rounded-lg ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"}`}>
          <Info className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
          {bg
            ? "Всички зададени цели са в посока, която не подобрява текущите стойности. Опитайте с по-амбициозни цели (напр. по-висок vit D, по-нисък hs-CRP, повече движение)."
            : "None of the targets improve over current values. Try more ambitious targets."}
        </div>
      )}

      {result && !result.unchanged && (
        <div className="space-y-3" data-testid="recovery-result">
          {/* Composite delta */}
          <div className={`p-3 rounded-xl ${isDark ? "bg-slate-800/70" : "bg-white ring-1 ring-slate-200"}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <div className={`text-[10px] uppercase font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>Прогнозирано BHI</div>
                <div className="mt-1">
                  <DeltaBadge
                    before={result.baseline?.bhi_composite}
                    after={result.projected?.bhi_composite}
                    larger
                  />
                </div>
              </div>
              {result.deltas?.biggest_gain && (
                <div className={`text-[12px] ${isDark ? "text-slate-300" : "text-slate-700"}`} data-testid="recovery-biggest-gain">
                  <TrendingUp className="inline w-4 h-4 mr-1 -mt-0.5 text-emerald-500" />
                  <b>{bg ? "Най-силен ефект:" : "Biggest gain:"}</b>{" "}
                  {COMPONENT_LABEL_BG[result.deltas.biggest_gain.component] || result.deltas.biggest_gain.component}{" "}
                  <span className="tabular-nums">{result.deltas.biggest_gain.before}→{result.deltas.biggest_gain.after}</span>{" "}
                  <span className="font-bold text-emerald-600">(+{result.deltas.biggest_gain.delta})</span>
                </div>
              )}
            </div>
          </div>

          {/* Component deltas */}
          {result.deltas?.by_component?.length > 0 && (
            <div>
              <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Промени по метаболитен компонент:" : "Per-component changes:"}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="recovery-by-component-row">
                {result.deltas.by_component.map((c) => (
                  <span
                    key={c.component}
                    data-testid={`recovery-component-${c.component}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] ring-1 ${
                      c.delta > 0
                        ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                        : "bg-rose-50 text-rose-800 ring-rose-200"
                    }`}
                  >
                    <span className="font-semibold">{COMPONENT_LABEL_BG[c.component] || c.component}</span>
                    <span className="tabular-nums">{c.before}→{c.after}</span>
                    <span className="font-bold">({c.delta > 0 ? `+${c.delta}` : `−${Math.abs(c.delta)}`})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Twin projection */}
          {result.twin_blend_projection?.length > 0 && result.twin_blend_projection[0]?.expected_shift !== 0 && (
            <div>
              <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Очаквано влияние върху близнаци (BHI blend 15%):" : "Expected twin impact (BHI blend 15%):"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {result.twin_blend_projection.map((t) => (
                  <span
                    key={t.twin}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] ring-1 ${
                      t.expected_shift > 0
                        ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                        : "bg-slate-50 text-slate-600 ring-slate-200"
                    }`}
                  >
                    {TWIN_LABEL_BG[t.twin]}
                    <span className="font-bold">{t.expected_shift > 0 ? `+${t.expected_shift}` : t.expected_shift}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Applied interventions summary */}
          {result.applied_interventions?.length > 0 && (
            <div>
              <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Приложени интервенции:" : "Applied interventions:"}
              </div>
              <ul className={`text-[11px] space-y-0.5 ml-4 list-disc ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {result.applied_interventions.map((it) => (
                  <li key={it.key}>
                    <b>{it.label_bg}:</b> <span className="tabular-nums">{Number(it.from).toFixed(1)} → {Number(it.to).toFixed(1)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Deep-link to BTRE + BPP for actionable plans */}
          <div className={`p-3 rounded-xl border-l-4 ${isDark ? "bg-emerald-500/10 border-emerald-500/50" : "bg-emerald-50 border-emerald-400"}`}>
            <div className={`text-[12px] mb-2 ${isDark ? "text-slate-200" : "text-slate-800"}`}>
              <b>{bg ? "От диагноза към възстановяване:" : "From diagnosis to recovery:"}</b>{" "}
              {bg
                ? "Отворете конкретните планове, които реализират тези цели чрез хранене, нутрицевтици и лабораторни цели."
                : "Open the concrete plans that operationalise these targets via nutrition, supplements, and lab goals."}
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="#phase-plan"
                data-testid="recovery-open-btre"
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold ${
                  isDark ? "bg-emerald-500/25 text-emerald-100 hover:bg-emerald-500/40" : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const el =
                    document.querySelector('[data-testid="section-tissue-plan"]') ||
                    document.getElementById("phase-plan");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {bg ? "Отвори BTRE план" : "Open BTRE plan"} <ArrowRight className="w-3 h-3" />
              </Link>
              <Link
                to="#phase-plan"
                data-testid="recovery-open-bpp"
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold ${
                  isDark ? "bg-slate-700 text-slate-100 hover:bg-slate-600" : "bg-white ring-1 ring-emerald-400 text-emerald-800 hover:bg-emerald-50"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const el =
                    document.querySelector('[data-testid="section-prevention-program"]') ||
                    document.getElementById("phase-plan");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {bg ? "Отвори BPP програма" : "Open BPP program"} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>

          <div className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {bg
              ? "⚠ Симулацията НЕ записва нищо. Резултатите са образователни."
              : "⚠ Simulation does not write anything. Educational only."}
          </div>
        </div>
      )}
    </section>
  );
}
